import {
  CollectionBoxes,
  CollectionBoxItem,
  getPathWithParams,
  useLogger,
  UseLogger,
  useRedirect,
  UseRedirect,
  UseState
} from '@on-arte/ui';
import React, { useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { QueryClient, useQuery, useQueryClient } from 'react-query';

import { getCollections } from '@onArte/frontend/api/requests';
import { QueryKey } from '@onArte/frontend/enums';
import { FrontendApiError } from '@onArte/frontend/models';
import { ssrQueryKey } from '@onArte/shared/constants';
import { RouteNameEnum } from '@onArte/shared/enums';
import { Collection, CollectionCard } from '@onArte/shared/interfaces';
import { getRouteDetailsByName } from '@onArte/shared/utils';

import { CollectionsContainer, Container, StyledPageTitle } from './collectionsList.styled';

const mapCollectionToBox = (item: CollectionCard): CollectionBoxItem => ({
  label: item.name,
  image: item.coverPhoto,
  linkData: {
    internalPath: item.route?.prettyUrl
      ?? getPathWithParams(getRouteDetailsByName(RouteNameEnum.Collection)?.url ?? '/', { id: item.id })
  }
});

export const CollectionsListView: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirect = useRedirect();
  // Stan `null`, nie pusta tablica: pusta tablica nie odróżnia „jeszcze nie wczytane"
  // od „wczytane i puste". Gdy praca zniknie między odpowiedzią serwera a zapytaniem
  // przeglądarki, widok zostawałby przy danych z serwera na stałe.
  const [collections, setCollections]: UseState<CollectionBoxItem[] | null> = useState<CollectionBoxItem[] | null>(null);
  const { logger }: UseLogger = useLogger();

  useQuery(
    [QueryKey.Collections],
    (): Promise<Collection[]> => getCollections(),
    {
      onSuccess: (data: Collection[]): void => {
        setCollections(data.map(mapCollectionToBox));
      },
      onError: (error: FrontendApiError): void => logger(QueryKey.Collections, error)
    }
  );

  /**
   * Serwer wypełnia pamięć podręczną pod stabilnym kluczem (ssrPrefetch.helper.ts).
   * Widok trzyma selekcje w stanie lokalnym wypełnianym w `onSuccess`, a ten przy
   * renderowaniu serwerowym się nie wykonuje — bez tej ścieżki /selekcja wychodziła
   * z SSR bez ani jednego odnośnika do selekcji.
   */
  const queryClient: QueryClient = useQueryClient();
  const ssrCollections: CollectionBoxItem[] = useMemo(
    (): CollectionBoxItem[] =>
      (queryClient.getQueryData<CollectionCard[]>(ssrQueryKey(QueryKey.Collections)) ?? []).map(mapCollectionToBox),
    []
  );

  return (
    <Container>
      <StyledPageTitle 
        pageTitleSettings={{
          title: t('onarte.website.collectionsListView.pageTitle.title'),
          linkLabel: t('onarte.website.collectionsListView.pageTitle.link'),
          linkAction: (): void => redirect(getRouteDetailsByName(RouteNameEnum.ArtworksList)?.url ?? '/'),
          withUnderline: false,
          description: t('onarte.website.collectionsListView.pageTitle.description')
        }}
      />
      <CollectionsContainer>
        <CollectionBoxes items={collections ?? ssrCollections} />
      </CollectionsContainer>
    </Container>
  );
};
